/**
 * What an enemy actually does, in words, derived from its definition (playtest request:
 * "on next wave hover over to see what they do").
 *
 * DERIVED rather than authored. A hand-written line per archetype would be nicer prose
 * and would start lying the first time a number moved — and numbers move here every
 * balance pass. Reading the def means the tooltip is a view of the data the simulation
 * is about to use, not a second copy of it that has to be remembered.
 *
 * Pure and UI-free so it can be tested without mounting anything.
 */
import type { EnemyDef } from '@prism/content';

const TICKS_PER_SECOND = 60;

export interface EnemyBrief {
  name: string;
  /** One line of raw numbers — HP, armour, speed, what a leak costs. */
  stats: string;
  /** Everything the archetype does beyond walking, one clause each. Possibly empty. */
  traits: string[];
}

/**
 * Trim a number for display without lying about it.
 *
 * One decimal was not enough: Prism walks at 0.25 tiles/s and this rounded it to "0.3",
 * while the Discovery table beside it printed the real value — the same screen showing a
 * stat two different ways, which is worse than either version alone. Two decimals covers
 * every authored number, and the trailing-zero trim keeps whole values whole.
 */
function short(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/\.?0+$/, '');
}

function joinList(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

export function enemyBrief(def: EnemyDef, nameOf: (id: string) => string): EnemyBrief {
  const stats = [
    `${short(def.hp)} HP`,
    def.armor > 0 ? `${short(def.armor)} armour` : null,
    `${short(def.speed)} tiles/s`,
    `${short(def.leakDamage)} Core damage if it leaks`,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');

  const traits: string[] = [];

  if (def.flags.includes('flying')) {
    traits.push('Flies — ignores the path and makes straight for the Core.');
  }
  if (def.armor >= 10) {
    // Armour is a FLAT subtraction per hit, so it is a different problem from raw HP:
    // fast small-hit towers are what it beats, and saying so is the counterplay.
    traits.push('Heavily armoured — flat reduction per hit, so fast small hits bounce.');
  }
  if (def.splitInto) {
    const child = nameOf(def.splitInto.id);
    traits.push(`Splits into ${def.splitInto.count}× ${child} when killed.`);
  }
  if (def.flags.includes('mender') || def.healRadius !== undefined) {
    const rate = def.healPercentPerSecond;
    traits.push(
      rate === undefined
        ? 'Heals nearby enemies — kill it first.'
        // Authored as a FRACTION, printed as a percent. It used to pass the fraction
        // straight into a "%" label and round it, so Chorus — which heals 3% a second —
        // advertised "0.0% max HP per second", i.e. a healer that heals nothing.
        : `Heals nearby enemies for ${short(rate * 100)}% max HP per second — kill it first.`,
    );
  }
  // The shield CYCLE, which is a different thing from `shieldPerHit` below: a bar that
  // breaks and drops the enemy, rather than a flat absorption on every hit. It is Prism's
  // entire fight and the page did not mention it at all.
  if (def.shieldHp !== undefined && def.shieldDownTicks !== undefined) {
    const window = short(def.shieldDownTicks / TICKS_PER_SECOND);
    traits.push(
      `Carries a ${short(def.shieldHp)}-point shield — while it holds, its health cannot ` +
        `be touched. Break it and it drops, frozen and exposed for ${window}s, then raises ` +
        'a full one again.',
    );
  }
  if (def.shieldPerHit !== undefined) {
    traits.push(`Shielded — absorbs ${short(def.shieldPerHit)} from every single hit.`);
  }
  if (def.flags.includes('leaper') && def.leapEveryTicks !== undefined) {
    const every = short(def.leapEveryTicks / TICKS_PER_SECOND);
    const tiles = short(def.leapTiles ?? 0);
    traits.push(`Leaps ${tiles} tiles forward every ${every}s — a slow or a stun grounds it.`);
  }
  if (def.statusImmunities && def.statusImmunities.length > 0) {
    traits.push(`Immune to ${joinList(def.statusImmunities)}.`);
  }
  if (def.emergeTicks !== undefined) {
    const seconds = short(def.emergeTicks / TICKS_PER_SECOND);
    traits.push(`Rises out of the ground over ${seconds}s — untouchable until it is up.`);
  }
  if (def.teleportEveryTicks !== undefined) {
    const every = short(def.teleportEveryTicks / TICKS_PER_SECOND);
    traits.push(`Blinks across the Core's row every ${every}s — it changes lane, not distance.`);
  }
  if (def.frontalReduction !== undefined) {
    // Both halves stated. The counter IS the information here, unlike the Disabler's
    // reach — an armoured front that you cannot find out how to get around is just a
    // tougher enemy wearing a costume.
    traits.push(
      `Plated across the front — shots from ahead land for ` +
        `${short(100 - def.frontalReduction * 100)}% of their damage. Hit it from the ` +
        `side or behind, or poison it: rot has no direction to be turned from.`,
    );
  }
  if (def.disableRange !== undefined && def.disableEveryTicks !== undefined) {
    const every = short(def.disableEveryTicks / TICKS_PER_SECOND);
    const held = short((def.disableTicks ?? 0) / TICKS_PER_SECOND);
    // The radius is named and the RANGE is not, deliberately: how far it can reach is the
    // thing you are meant to learn by losing a board to it, but what the shot does once it
    // lands should never be a mystery.
    traits.push(
      `Every ${every}s it fires on your strongest tower — everything within ` +
        `${short(def.disableRadius ?? 0)} tiles of it stops firing for ${held}s. Kill it first.`,
    );
  }
  if (def.flags.includes('boss')) {
    traits.push('Boss — one of these ends the wave, and the run if it reaches you.');
  }

  return { name: def.name, stats, traits };
}
